/**
 * Travel Locations Management Component
 * Coláiste Lurgan Management System - Travel Location CRUD operations
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { useTravelLocations, useDeleteTravelLocation } from '../../hooks/useTravelLocations';
import TravelLocationModal from './TravelLocationModal';
import {
  MapPin,
  Plus,
  Pencil,
  Trash2,
  Navigation,
  Users
} from 'lucide-react';

export function TravelLocationsManagement() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState(null);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [locationToEdit, setLocationToEdit] = useState(null);

  // Mutations
  const deleteTravelLocationMutation = useDeleteTravelLocation();
  // Travel location mutation available for debugging

  // Get travel locations data with better error handling
  const {
    data: travelLocationsResponse,
    isLoading,
    isError,
    error
  } = useTravelLocations({}, {
    // Graceful error handling - don't treat empty DB as error
    retry: (failureCount, error) => {
      // Don't retry on 404 (no locations) or 500 from empty DB
      if (error?.response?.status === 404) return false;
      if (error?.response?.status === 500) return false;
      return failureCount < 3;
    },
    // Default to empty state if API fails
    onError: (error) => {
      // Travel Locations API error handled by error state
    }
  });

  // Handle both paginated and non-paginated response formats
  const travelLocations = Array.isArray(travelLocationsResponse)
    ? travelLocationsResponse
    : (travelLocationsResponse?.results || []);

  // Check if location can be deleted (no students assigned)
  const canDeleteLocation = (location) => {
    // Location can be deleted if no students are assigned to it
    const canDelete = (location.student_count || 0) === 0;
    return canDelete;
  };

  // Handle delete location
  const handleDeleteLocation = (location) => {
    setLocationToDelete(location);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteLocation = async () => {
    if (!locationToDelete) return;

    try {
      await deleteTravelLocationMutation.mutateAsync(locationToDelete.uuid);
      toast({
        title: t('travelLocations.deleted'),
        description: t('travelLocations.deleteSuccess', { name: locationToDelete.name }),
      });
      setDeleteDialogOpen(false);
      setLocationToDelete(null);
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('travelLocations.deleteError'),
        variant: 'destructive',
      });
    }
  };

  // Handle add location
  const handleAddLocation = () => {
    setLocationToEdit(null);
    setLocationModalOpen(true);
  };

  // Handle edit location
  const handleEditLocation = (location) => {
    setLocationToEdit(location);
    setLocationModalOpen(true);
  };

  // Handle location modal success
  const handleLocationModalSuccess = (locationData, isEditing) => {
    if (isEditing) {
      toast({
        title: t('travelLocations.updated'),
        description: t('travelLocations.updateSuccess', { name: locationData.name }),
      });
    } else {
      toast({
        title: t('travelLocations.created'),
        description: t('travelLocations.createSuccess', { name: locationData.name }),
      });
    }
  };

  // Format address for display
  const formatAddress = (address) => {
    if (!address || address.trim() === '') return '—';
    return address.length > 50 ? `${address.substring(0, 47)}...` : address;
  };

  // Debug data available: travelLocationsResponse, isLoading, isError, travelLocations, error

  // Only show error if it's a real error (not just empty database)
  if (isError && error?.response?.status !== 500) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <p className="text-destructive">
            {t('error.loadingTravelLocations', 'Error loading travel locations. Please try again.')}
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            Error: {error?.response?.status} - {error?.message}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                {t('travelLocations.management.title', 'Travel Locations')}
              </CardTitle>
              <CardDescription>
                {t('travelLocations.management.description', 'Manage student pickup and drop-off locations')}
              </CardDescription>
            </div>
            <Button className="gap-2" onClick={handleAddLocation}>
              <Plus className="h-4 w-4" />
              {t('travelLocations.actions.addLocation', 'Add Location')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                {t('loading.travelLocations', 'Loading travel locations...')}
              </p>
            </div>
          ) : travelLocations.length === 0 ? (
            <div className="text-center py-8">
              <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {t('travelLocations.empty', 'No travel locations configured yet. Add your first location to get started.')}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('travelLocations.fields.name', 'Location Name')}</TableHead>
                  <TableHead>{t('travelLocations.fields.address', 'Address')}</TableHead>
                  <TableHead>{t('travelLocations.fields.students', 'Students')}</TableHead>
                  <TableHead>{t('travelLocations.fields.status', 'Status')}</TableHead>
                  <TableHead className="text-right">{t('common.actions', 'Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {travelLocations.map((location) => (
                  <TableRow key={location.uuid}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Navigation className="h-4 w-4 text-blue-600" />
                        <div>
                          <div className="font-medium">{location.name}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {formatAddress(location.address)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3 text-green-600" />
                          <span className="font-medium">{location.student_count || 0}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {location.student_count === 1 ? 'student' : 'students'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={location.is_active ? 'default' : 'secondary'}>
                        {location.is_active
                          ? t('travelLocations.status.active', 'Active')
                          : t('travelLocations.status.inactive', 'Inactive')
                        }
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1"
                          onClick={() => handleEditLocation(location)}
                        >
                          <Pencil className="h-3 w-3" />
                          {t('common.edit', 'Edit')}
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1 text-red-600 hover:text-red-800"
                          disabled={!canDeleteLocation(location)}
                          onClick={() => handleDeleteLocation(location)}
                        >
                          <Trash2 className="h-3 w-3" />
                          {t('common.delete', 'Delete')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('travelLocations.delete.title', 'Delete Travel Location')}
            </DialogTitle>
            <DialogDescription>
              {t('travelLocations.delete.message', 'Are you sure you want to delete "{{locationName}}"? This action cannot be undone.', { locationName: locationToDelete?.name })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteLocation}
              disabled={deleteTravelLocationMutation.isPending}
            >
              {deleteTravelLocationMutation.isPending ? t('common.deleting', 'Deleting...') : t('common.delete', 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Travel Location Creation/Edit Modal */}
      <TravelLocationModal
        isOpen={locationModalOpen}
        onClose={() => setLocationModalOpen(false)}
        travelLocation={locationToEdit}
        onSuccess={handleLocationModalSuccess}
      />
    </>
  );
}

export default TravelLocationsManagement;